import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Typography,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Add,
  Close,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  Delete as DeleteIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon
} from '@mui/icons-material';
import TagCoverGrid from '../../components/organisms/TagCoverGrid/TagCoverGrid';
import { useTagSettings } from '../../context/TagSettingsContext';
import ConfirmDialog from '../../components/molecules/ConfirmDialog';
import PageToolbar from '../../components/organisms/PageToolbar';
import ModernToast from '../../components/molecules/ModernToast';
import api from '../../services/api';
import CreateTagDialog from '../../components/organisms/CreateTagDialog';
import TagFormDialog from '../../components/molecules/TagFormDialog';

// Opções de ordenação para Tags
const TAG_SORT_OPTIONS = [
  { value: 'name_asc', label: 'Nome (A-Z)' },
  { value: 'name_desc', label: 'Nome (Z-A)' },
  { value: 'articles_desc', label: 'Mais Artigos' },
  { value: 'articles_asc', label: 'Menos Artigos' },
  { value: 'date_desc', label: 'Mais Recentes' },
  { value: 'date_asc', label: 'Mais Antigas' }
];

export const TagsCoverPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { tags, loading, createTag, updateTag, deleteTag, refreshTags } = useTagSettings();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [currentTag, setCurrentTag] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [tagToDelete, setTagToDelete] = useState(null);

  // Estados da toolbar
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState(() => localStorage.getItem('tags_sortBy') || 'name_asc');
  const [variant, setVariant] = useState(() => localStorage.getItem('tags_variant') || 'grid');

  // Estados de seleção
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);

  // No mobile, forçar visualização grid (list não funciona bem)
  useEffect(() => {
    if (isMobile && variant === 'list') {
      setVariant('grid');
    }
  }, [isMobile, variant]);

  // Filtrar e ordenar tags
  const filteredTags = useMemo(() => {
    let result = [...tags];

    // Filtrar por busca
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(tag =>
        tag.name.toLowerCase().includes(query) ||
        tag.description?.toLowerCase().includes(query)
      );
    }

    // Ordenar
    result.sort((a, b) => {
      switch (sortBy) {
        case 'name_asc':
          return a.name.localeCompare(b.name);
        case 'name_desc':
          return b.name.localeCompare(a.name);
        case 'articles_desc':
          return (b.articleCount || 0) - (a.articleCount || 0);
        case 'articles_asc':
          return (a.articleCount || 0) - (b.articleCount || 0);
        case 'date_desc':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'date_asc':
          return new Date(a.createdAt) - new Date(b.createdAt);
        default:
          return 0;
      }
    });

    return result;
  }, [tags, searchQuery, sortBy]);

  const handleCreateClick = () => {
    setFormData({
      name: '',
      description: ''
    });
    setIsCreateDialogOpen(true);
  };

  const handleEditClick = (tag) => {
    setCurrentTag(tag);
    setFormData({
      name: tag.name,
      description: tag.description || ''
    });
    setIsEditDialogOpen(true);
  };

  const handleDeleteClick = (tag) => {
    if (!tag || !tag._id) {
      console.error('Tentativa de remover tag inválida:', tag);
      setSnackbar({ open: true, message: 'Tag inválida para remoção.', severity: 'error' });
      return;
    }
    setTagToDelete(tag);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!tagToDelete || !tagToDelete._id) return;
    try {
      await deleteTag(tagToDelete._id);
      setSnackbar({ open: true, message: 'Tag excluída com sucesso!', severity: 'success' });
    } catch (err) {
      console.error('Erro ao remover tag:', err, tagToDelete);
      setSnackbar({ open: true, message: err.message || 'Erro ao excluir tag', severity: 'error' });
    } finally {
      setConfirmDialogOpen(false);
      setTagToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDialogOpen(false);
    setTagToDelete(null);
  };

  const handleCreateTagSubmit = async (tagData) => {
    if (!tagData?.name?.trim()) return;
    
    try {
      await api.post('/tags', tagData);
      setIsCreateDialogOpen(false);
      setSnackbar({ open: true, message: `Tag "${tagData.name.trim()}" criada com sucesso!`, severity: 'success' });
      // Atualizar a lista de tags
      refreshTags();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Erro ao criar tag', severity: 'error' });
    }
  };

  const handleEditTagSubmit = async (formData) => {
    try {
      await updateTag(currentTag._id, formData);
      setSnackbar({ open: true, message: 'Tag atualizada com sucesso!', severity: 'success' });
      setIsEditDialogOpen(false);
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Erro ao atualizar tag', severity: 'error' });
    }
  };

  const handleCloseSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));

  // Handlers da toolbar
  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleSortChange = (value) => {
    setSortBy(value);
    localStorage.setItem('tags_sortBy', value);
  };

  const handleVariantChange = (_, newVariant) => {
    if (newVariant) {
      setVariant(newVariant);
      localStorage.setItem('tags_variant', newVariant);
    }
  };

  // Handlers de seleção
  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    if (selectionMode) {
      setSelectedTags([]);
    }
  };

  const handleTagSelect = (tagId) => {
    setSelectedTags(prev => {
      if (prev.includes(tagId)) {
        return prev.filter(id => id !== tagId);
      } else {
        return [...prev, tagId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedTags.length === filteredTags.length) {
      setSelectedTags([]);
    } else {
      setSelectedTags(filteredTags.map(tag => tag._id));
    }
  };

  const handleBulkDelete = () => {
    if (selectedTags.length === 0) return;
    setBulkDeleteDialogOpen(true);
  };

  const handleConfirmBulkDelete = async () => {
    try {
      // Deletar todas as tags selecionadas
      await Promise.all(selectedTags.map(tagId => deleteTag(tagId)));
      setSnackbar({
        open: true,
        message: `${selectedTags.length} tag(s) excluída(s) com sucesso!`,
        severity: 'success'
      });
      setSelectedTags([]);
      setSelectionMode(false);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'Erro ao excluir tags',
        severity: 'error'
      });
    } finally {
      setBulkDeleteDialogOpen(false);
    }
  };

  const handleTagClick = (tag) => {
    if (selectionMode) {
      handleTagSelect(tag._id);
    } else {
      navigate(`/tags/${tag._id}/artigos`);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Toolbar */}
      <PageToolbar
        title="Tags"
        searchValue={searchQuery}
        onSearchChange={handleSearch}
        searchPlaceholder="Buscar tags..."
        sortValue={sortBy}
        onSortChange={handleSortChange}
        sortOptions={TAG_SORT_OPTIONS}
        viewValue={variant}
        onViewChange={handleVariantChange}
        viewOptions={[
          { value: 'grid', icon: ViewModuleIcon, tooltip: 'Visualização em grade' },
          { value: 'list', icon: ViewListIcon, tooltip: 'Visualização em lista' }
        ]}
        resultCount={filteredTags.length}
        resultLabel="tag(s) encontrada(s)"
        fixed={true}
        onNewItem={handleCreateClick}
        newItemLabel="Nova Tag"
        extraControls={
          <>
            {selectionMode && selectedTags.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDelete}
                sx={{
                  height: 36,
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.8125rem',
                  borderRadius: 1
                }}
              >
                Excluir ({selectedTags.length})
              </Button>
            )}
            <Button
              variant={selectionMode ? "contained" : "outlined"}
              color={selectionMode ? "secondary" : "primary"}
              onClick={toggleSelectionMode}
              startIcon={selectionMode ? <Close /> : <CheckBoxIcon />}
              sx={{
                height: 36,
                px: 1.5,
                py: 0.5,
                fontSize: '0.8125rem',
                borderRadius: 1
              }}
            >
              {selectionMode ? 'Cancelar' : 'Selecionar'}
            </Button>
            {selectionMode && (
              <Tooltip title={selectedTags.length === filteredTags.length ? "Desmarcar todos" : "Selecionar todos"}>
                <IconButton
                  onClick={handleSelectAll}
                  color="primary"
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1
                  }}
                >
                  {selectedTags.length === filteredTags.length ? (
                    <CheckBoxIcon />
                  ) : (
                    <CheckBoxOutlineBlankIcon />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </>
        }
        headerActions={
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={handleCreateClick}
            sx={{
              height: 36,
              px: 1.5,
              py: 0.5,
              fontSize: '0.8125rem',
              borderRadius: 1
            }}
          >
            Nova Tag
          </Button>
        }
      />

      {/* Loading indicator */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress />
        </Box>
      ) : filteredTags.length === 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Nenhuma tag encontrada
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchQuery ? 'Tente ajustar sua busca' : 'Crie sua primeira tag clicando em "Nova Tag"'}
          </Typography>
        </Box>
      ) : (
        /* Grid de tags */
        <TagCoverGrid
          tags={filteredTags}
          variant={variant}
          onTagClick={handleTagClick}
          onEditTag={handleEditClick}
          onDeleteTag={handleDeleteClick}
          selectionMode={selectionMode}
          selectedTags={selectedTags}
          onTagSelect={handleTagSelect}
        />
      )}

      {/* Dialog para criar tag */}
      <CreateTagDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSave={handleCreateTagSubmit}
      />

      {/* Dialog para editar tag */}
      <TagFormDialog
        open={isEditDialogOpen}
        tag={currentTag}
        onClose={() => setIsEditDialogOpen(false)}
        onSave={handleEditTagSubmit}
      />

      {/* Dialog de confirmação para exclusão individual */}
      <ConfirmDialog
        open={confirmDialogOpen}
        title="Excluir Tag"
        message={`Tem certeza que deseja excluir a tag "${tagToDelete?.name}"?`}
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      {/* Dialog de confirmação para exclusão em massa */}
      <ConfirmDialog
        open={bulkDeleteDialogOpen}
        title="Excluir Tags Selecionadas"
        message={`Tem certeza que deseja excluir ${selectedTags.length} tag(s) selecionada(s)?`}
        confirmText="Excluir Todas"
        cancelText="Cancelar"
        onConfirm={handleConfirmBulkDelete}
        onCancel={() => setBulkDeleteDialogOpen(false)}
      />

      <ModernToast
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleCloseSnackbar}
        duration={4000}
        position="bottom-center"
      />
    </Box>
  );
};

export default TagsCoverPage;
